import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd } from "@theme/utilities";
import { StandardEvents } from "@shopify/events";

class StickyAddToCartComponent extends Component {
  requiredRefs = ["stickyBar", "addToCartButton", "quantityDisplay", "quantityNumber"];
  #resetTimeout;
  #isStuck = false;
  #scrollAnimationFrame = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#setupScrollThreshold(signal);
    this.closest(".shopify-section")?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#scrollAnimationFrame !== null) {
      cancelAnimationFrame(this.#scrollAnimationFrame);
    }
  }

  #setupScrollThreshold(signal) {
    const pageWrapper = document.querySelector(".page-wrapper");
    const productForm = this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    const updateStickyBar = () => {
      const pageWrapperScrolls =
        pageWrapper instanceof HTMLElement && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1;
      const scrollElement = pageWrapperScrolls ? pageWrapper : document.scrollingElement;

      if (!scrollElement) return;

      const maximumScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
      if (maximumScroll <= 0) return;

      const scrollProgress = scrollElement.scrollTop / maximumScroll;
      if (scrollProgress >= 0.5 && !this.#isStuck) {
        this.#showStickyBar();
      } else if (scrollProgress < 0.5 && this.#isStuck) {
        this.#hideStickyBar();
      }
    };

    const handleScroll = () => {
      if (this.#scrollAnimationFrame !== null) return;

      this.#scrollAnimationFrame = requestAnimationFrame(() => {
        this.#scrollAnimationFrame = null;
        updateStickyBar();
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true, signal });
    pageWrapper?.addEventListener("scroll", handleScroll, { passive: true, signal });
    updateStickyBar();
  }

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = "true";
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector(".header-actions__cart-icon");
    if (this.refs.addToCartButton.dataset.added !== "true") {
      this.refs.addToCartButton.dataset.added = "true";
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = document.createElement("fly-to-cart");
    flyToCartElement.classList.add("fly-to-cart--sticky");
    flyToCartElement.style.setProperty("background-image", `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = "true";
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute("data-added");
    }, 800);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest("product-card")) return;

    const { optionValueId } = event.detail ?? {};
    if (optionValueId) this.dataset.currentVariantId = optionValueId;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;
        if (productId && productId !== this.dataset.productId) return;

        const newStickyAddToCart = html.querySelector("sticky-add-to-cart");
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        const currentStuck = this.refs.stickyBar.getAttribute("data-stuck") || "false";
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
        this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        if (variant?.id) this.dataset.currentVariantId = variant.id;

        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) this.#handleVariantUnavailable();
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.warn("[sticky-add-to-cart] Event promise rejected:", error);
        }
      });
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked"))
      .map((option) => option.value)
      .filter((value) => value !== "")
      .join(" / ");

    if (selectedOptions) variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = "false";
      }
    };

    if ("promise" in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  #handleQuantityUpdate = (event) => {
    if (!event.detail.cartLine) {
      this.#currentQuantity = event.detail.quantity;
      this.#updateButtonText();
    }
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = Number.parseInt(this.dataset.initialQuantity || "1", 10) || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? "inline" : "none";
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
